/**
 * ThirdPartyIntegrations — Gestor de credenciales de integraciones 3rd party.
 *
 * Por ahora solo Meteosim RMS. Las credenciales se cifran con Fernet en
 * backend (servicio `integrations_store.py`). El frontend NUNCA recibe
 * los valores; sólo el estado (configured: bool, updated_by, updated_at).
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Cloud, Lock, Eye, EyeSlash, FloppyDisk, Trash, Check, Warning,
} from "@phosphor-icons/react";

export default function ThirdPartyIntegrations() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/integrations");
      setItems(r.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudieron cargar las integraciones");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <section className="bg-white border border-border rounded-md p-5 space-y-4"
             data-testid="third-party-integrations-section">
      <header className="flex items-center gap-2">
        <span className="icon-chip icon-chip-brand !w-8 !h-8">
          <Cloud className="w-4 h-4" weight="regular" />
        </span>
        <div>
          <h2 className="section-title">Integraciones de terceros</h2>
          <p className="help-text">
            Credenciales corporativas para servicios externos. Se cifran con
            AES-128 (Fernet) en la base de datos.
          </p>
        </div>
      </header>

      {loading && <p className="help-text">Cargando…</p>}
      {!loading && items.length === 0 && (
        <p className="help-text">No hay integraciones disponibles.</p>
      )}
      {!loading && items.map((it) => (
        <IntegrationCard key={it.provider} item={it} onChange={load} />
      ))}
    </section>
  );
}

function IntegrationCard({ item, onChange }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);

  const configured = !!item.status?.configured;

  const reset = () => { setEmail(""); setPassword(""); setShowPwd(false); };

  const save = async () => {
    if (!email && !password) {
      toast.error("Indica email o contraseña");
      return;
    }
    setSaving(true);
    try {
      const body = {};
      if (email) body.email = email;
      if (password) body.password = password;
      await api.put(`/integrations/${item.provider}`, body);
      toast.success(`Credenciales de ${item.label} guardadas (cifradas)`);
      reset();
      setOpen(false);
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Borrar credenciales de ${item.label}? La integración dejará de funcionar.`)) return;
    setRemoving(true);
    try {
      await api.delete(`/integrations/${item.provider}`);
      toast.success("Credenciales borradas");
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al borrar");
    } finally {
      setRemoving(false);
    }
  };

  return (
    <article className="border border-border rounded-md p-4"
             data-testid={`integration-card-${item.provider}`}>
      <header className="flex items-start gap-3">
        <div className="flex-1">
          <h3 className="text-foreground font-semibold text-[14px]">{item.label}</h3>
          <p className="help-text mt-0.5">{item.description}</p>
          <div className="mt-2 flex items-center gap-2 text-[11.5px]">
            {configured ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-status-success-bg text-status-success-fg font-semibold"
                    data-testid={`integration-${item.provider}-status-configured`}>
                <Check className="w-3 h-3" weight="bold" /> Configurada
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-status-warning-bg text-status-warning-fg font-semibold"
                    data-testid={`integration-${item.provider}-status-not-configured`}>
                <Warning className="w-3 h-3" weight="bold" /> Sin configurar
              </span>
            )}
            {configured && item.status?.updated_by && (
              <span className="text-muted">
                · actualizada por <b>{item.status.updated_by}</b>
                {item.status.updated_at && (
                  <> el {new Date(item.status.updated_at).toLocaleString("es-ES")}</>
                )}
              </span>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setOpen((o) => !o)}
                  data-testid={`integration-${item.provider}-toggle-btn`}
                  className="bg-foreground text-white text-[12px] h-8 px-3 rounded-md font-semibold inline-flex items-center gap-1.5">
            <Lock className="w-3.5 h-3.5" weight="bold" />
            {configured ? "Cambiar" : "Configurar"}
          </button>
          {configured && (
            <button onClick={remove} disabled={removing}
                    data-testid={`integration-${item.provider}-delete-btn`}
                    className="border border-border text-status-error-fg text-[12px] h-8 px-3 rounded-md font-semibold inline-flex items-center gap-1.5 disabled:opacity-40">
              <Trash className="w-3.5 h-3.5" weight="regular" />
              {removing ? "…" : "Borrar"}
            </button>
          )}
        </div>
      </header>

      {open && (
        <div className="mt-4 pt-4 border-t border-border space-y-3"
             data-testid={`integration-${item.provider}-form`}>
          <div>
            <label className="field-label block mb-1">Email / Usuario corporativo</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                   placeholder={configured ? "(dejar vacío = sin cambios)" : "perito@dobilas.es"}
                   data-testid={`integration-${item.provider}-email-input`}
                   className="w-full" autoComplete="off" />
          </div>
          <div>
            <label className="field-label block mb-1">Contraseña</label>
            <div className="relative">
              <input type={showPwd ? "text" : "password"} value={password}
                     onChange={(e) => setPassword(e.target.value)}
                     placeholder={configured ? "(dejar vacío = sin cambios)" : "••••••••"}
                     data-testid={`integration-${item.provider}-password-input`}
                     className="w-full !pr-9" autoComplete="new-password" />
              <button type="button" onClick={() => setShowPwd((s) => !s)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-muted hover:text-foreground"
                      data-testid={`integration-${item.provider}-password-toggle`}>
                {showPwd ? <EyeSlash className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <p className="help-text mt-1">
              Se cifra al instante con AES-128 (Fernet) usando la clave maestra del servidor. No queda en logs ni en la respuesta.
            </p>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={() => { reset(); setOpen(false); }}
                    className="border border-border text-foreground text-[12px] h-9 px-4 rounded-md font-semibold">
              Cancelar
            </button>
            <button onClick={save} disabled={saving || (!email && !password)}
                    data-testid={`integration-${item.provider}-save-btn`}
                    className="bg-foreground text-white text-[12px] h-9 px-4 rounded-md font-semibold inline-flex items-center gap-1.5 disabled:opacity-40">
              <FloppyDisk className="w-3.5 h-3.5" weight="bold" />
              {saving ? "Guardando…" : "Guardar cifradas"}
            </button>
          </div>
        </div>
      )}
    </article>
  );
}
